import { useContext } from "react";
import { WifiKeyDto } from "../../../dtos/WifiKeyDto";
import VspPortalContext from "../../../store/VspPortalContext";
import { NotificationTypes } from "../../../enums/NotificationTypes";
import { HttpMethods } from "../../../enums/HttpMethods";
import { VspPortalActions } from "../../../store/VspPortalActions";
import { VisitorDto } from "../../../dtos/VisitorDto";
import { DeviceDto } from "../../../dtos/DeviceDto";
import { ResetWifiKeyDto } from "../../../dtos/ResetWifiKeyDto";
import { CreateResidentWifiKeyDto } from "../../../dtos/CreateResidentWifiKeyDto";
import { ByWifiKeyDto } from "../../../dtos/ByWifiKeyDto";
import { UpdateByWifiKeyDto } from "../../../dtos/UpdateByWifiKeyDto";
import { DeviceSessionDto } from "../dtos/DeviceSessionDto";
import { IssuedKeyDto } from "../dtos/IssuedKeyDto";
import { VillageDeviceOccurrenceDto } from "../dtos/VillageDeviceOccurrenceDto";
import { VillageDeviceAuthDto } from "../dtos/VillageDeviceAuthDto";
import { VillageIssuedKeysDto } from "../dtos/VillageIssuedKeysDto";
import { useVspPortalHttp } from "../../../hooks/useVspPortalHttp";


export const useVillageWifiHttp = ( ): {

    createResidentWifiKey( createResidentWifiKeyDto : CreateResidentWifiKeyDto ) : Promise< WifiKeyDto | null >
    , getDeviceSessions( mac: string, residentId: number ): Promise< DeviceSessionDto[ ] | null >
    , getDeviceWifiKey( device: DeviceDto ) : Promise< WifiKeyDto | null >
    , getIssuedKeys( dateFrom: string, dateTo: string ): Promise< IssuedKeyDto[ ] | null >
    , getLatestDeviceSessions( residentId: number ): Promise< DeviceSessionDto[ ] | null >
    , getResidentByWifiKey( wifiKey: string ) : Promise< ByWifiKeyDto | null >
    , getResidentWifiKey( assetCode: string, employeeId: string ) : Promise< WifiKeyDto | null >
    , getVillageDeviceAuths( dateFrom: string, dateTo: string ): Promise< VillageDeviceAuthDto[ ] | null >
    , getVillageDeviceOccurrences( dateFrom: string, dateTo: string ): Promise< VillageDeviceOccurrenceDto[ ] | null >
    , getVillageIssuedKeys( dateOn: string, village: string ): Promise< VillageIssuedKeysDto[ ] | null >
    , getVisitorWifiKey( visitor: VisitorDto ) : Promise< WifiKeyDto | null >
    , resetResidentWifiKey( resetWifiKey: ResetWifiKeyDto ) : Promise< WifiKeyDto | null >
    , updateByWifiKey( updateByWifiKey : UpdateByWifiKeyDto ) : Promise< boolean >

} => {

    const { dispatch } = useContext( VspPortalContext );
    const { fetchData, getHttpOptions } = useVspPortalHttp( );
    
    
    const createResidentWifiKey = async( createResidentWifiKeyDto : CreateResidentWifiKeyDto ) : Promise< WifiKeyDto | null > => {

        const options = await getHttpOptions ( HttpMethods.Post, JSON.stringify( createResidentWifiKeyDto ) );

        const query = `${process.env.REACT_APP_API_URL}/wifi/resident-wifi-key`;

        const response = await fetchData( options, query  );

        if ( response == null || response.response == null ) {

            dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Error });
            dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Error creating resident Wi-Fi key' });
            return null;

        }

        return await response.response?.json( );

    };


    const getDeviceSessions = async( mac: string, residentId: number ) : Promise< DeviceSessionDto[ ] | null > => {

        const options = await getHttpOptions ( HttpMethods.Get, undefined );
        const query = `${process.env.REACT_APP_API_URL}/wifi/device-sessions?mac=${ mac }&residentId=${ residentId }`;

        const response = await fetchData( options, query  );

        if ( response == null || response.statusCode !== 200 ) {

            dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Error });
            dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Error fetching device sessions' });
            return null;

        }

        return await response.response?.json( );

    };


    const getDeviceWifiKey = async( device: DeviceDto ) : Promise< WifiKeyDto | null > => {

        
        const options = await getHttpOptions ( HttpMethods.Post, JSON.stringify( device ) );
        const query = `${process.env.REACT_APP_API_URL}/wifi/device-wifi-key`;

        const response = await fetchData( options, query  );

        if ( response == null || response.response == null ) {

            dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Error });
            dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Error fetching device Wi-Fi key' });
            return null;

        }

        return await response.response?.json( );

    };
    

    const getIssuedKeys = async( dateFrom: string, dateTo: string ): Promise< IssuedKeyDto[ ] | null > => {

        
        const options = await getHttpOptions ( HttpMethods.Get, undefined );
        const query = `${process.env.REACT_APP_API_URL}/wifi/issued-keys?dateFrom=${ dateFrom }&dateTo=${ dateTo }`;

        const response = await fetchData( options, query  );

        if ( response == null || response.statusCode !== 200 ) {

            dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Error });
            dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Error fetching issued keys' });
            return null;

        }

        return await response.response?.json( );
        
    };


    const getLatestDeviceSessions = async( residentId: number ) : Promise< DeviceSessionDto[ ] | null> => {

        const options = await getHttpOptions ( HttpMethods.Get, undefined );
        const query = `${process.env.REACT_APP_API_URL}/wifi/device-sessions-latest?residentId=${ residentId }`;

        const response = await fetchData( options, query  );

        if ( response == null || response.statusCode !== 200 ) {

            dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Error });
            dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Error fetching device sessions' });
            return null;

        }

        return await response.response?.json( );
        
    };

    
    const getResidentByWifiKey = async( wifiKey : string ) : Promise< ByWifiKeyDto | null > => {

        const options = await getHttpOptions ( HttpMethods.Get, undefined );
        const encodedWifiKey : string = encodeURIComponent( wifiKey );
        const query = `${process.env.REACT_APP_API_URL}/wifi/resident-by-wifi-key?wifiKey=${encodedWifiKey}`;

        const response = await fetchData( options, query  );

        if ( response == null || response.response == null ) {

            dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Error });
            dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Error fetching resident by Wi-Fi key' });

            return null;

        }

        return await response.response?.json( );

    };
    
    
    const getResidentWifiKey = async( assetCode: string, employeeId: string ) : Promise< WifiKeyDto | null > => {

        const options = await getHttpOptions ( HttpMethods.Get, undefined );

        const query = `${process.env.REACT_APP_API_URL}/wifi/resident-wifi-key?assetCode=${assetCode}&employeeId=${employeeId}`;

        const response = await fetchData( options, query  );

        if ( response == null || response.response == null ) {

            dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Error });
            dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Error fetching resident Wi-Fi key' });

            return null;

        }

        return await response.response?.json( );

    };


    const getVillageDeviceAuths = async( dateFrom: string, dateTo: string ): Promise< VillageDeviceAuthDto[ ] | null > => {

        const options = await getHttpOptions ( HttpMethods.Get, undefined );
        const query = `${process.env.REACT_APP_API_URL}/wifi/village-device-auths?dateFrom=${ dateFrom }&dateTo=${ dateTo }`;

        const response = await fetchData( options, query  );

        if ( response == null || response.statusCode !== 200 ) {

            dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Error });
            dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Error fetching village device occurrences' });
            return null;

        }

        return await response.response?.json( );
        
    };

    const getVillageDeviceOccurrences = async( dateFrom: string, dateTo: string ): Promise< VillageDeviceOccurrenceDto[ ] | null > => {

        
        const options = await getHttpOptions ( HttpMethods.Get, undefined );
        const query = `${process.env.REACT_APP_API_URL}/wifi/village-device-occurrences?dateFrom=${ dateFrom }&dateTo=${ dateTo }`;

        const response = await fetchData( options, query  );

        if ( response == null || response.statusCode !== 200 ) {

            dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Error });
            dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Error fetching village device occurrences' });
            return null;

        }

        return await response.response?.json( );
        
    };

    const getVillageIssuedKeys = async( dateOn: string, village: string ): Promise< VillageIssuedKeysDto[ ] | null > => {
        
        
        const options = await getHttpOptions ( HttpMethods.Get, undefined );
        const query = `${process.env.REACT_APP_API_URL}/wifi/village-issued-keys?dateOn=${ dateOn }&village=${ village }`;

        const response = await fetchData( options, query  );

        if ( response == null || response.statusCode !== 200 ) {

            dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Error });
            dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Error fetching village issued keys' });
            return null;

        }

        return await response.response?.json( );

    };


    const getVisitorWifiKey = async( visitor: VisitorDto ) : Promise< WifiKeyDto | null > => {

        
        const options = await getHttpOptions ( HttpMethods.Post, JSON.stringify( visitor ) );

        const query = `${process.env.REACT_APP_API_URL}/wifi/visitor-wifi-key`;

        const response = await fetchData( options, query  );

        if ( response == null || response.response == null ) {

            dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Error });
            dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Error fetching visitor Wi-Fi key' });
            return null;

        }

        return await response.response?.json( );

    };


    const resetResidentWifiKey = async( resetWifiKey: ResetWifiKeyDto ) : Promise< WifiKeyDto | null > => {

        
        const options = await getHttpOptions ( HttpMethods.Put, JSON.stringify( resetWifiKey ) );
        const query = `${process.env.REACT_APP_API_URL}/wifi/reset-resident-wifi-key`;
        const response = await fetchData( options, query  );

        if ( response == null || response.response == null ) {

            dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Error });
            dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Error resetting resident Wi-Fi key' });
            return null;

        }

        return await response.response?.json( );

    };


    const updateByWifiKey = async( updateByWifiKey : UpdateByWifiKeyDto ) : Promise< boolean > => {

        
        const options = await getHttpOptions ( HttpMethods.Put, JSON.stringify( updateByWifiKey ) );
        const query = `${process.env.REACT_APP_API_URL}/wifi/update-by-wifi-key`;
        const response = await fetchData( options, query  );

        if ( response == null || response.statusCode !== 204 ) {

            dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Error });
            dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Error updating the record' });
            return false;

        }

        dispatch({ type: VspPortalActions.SetNotificationType, payload: NotificationTypes.Success });
        dispatch({ type: VspPortalActions.SetNotificationText, payload: 'Record successfully updated!' });

        return true;

    };

    
    return { 
        
        createResidentWifiKey
        , getDeviceSessions
        , getDeviceWifiKey
        , getIssuedKeys
        , getLatestDeviceSessions
        , getResidentByWifiKey
        , getResidentWifiKey
        , getVillageDeviceAuths
        , getVillageDeviceOccurrences
        , getVillageIssuedKeys
        , getVisitorWifiKey
        , resetResidentWifiKey
        , updateByWifiKey

    };

};

